import React, { useMemo } from 'react';
import {
	Container,
	Header,
	Box,
	ColumnLayout,
	SpaceBetween,
} from '@amzn/awsui-components-console';
import Popover from '@amzn/awsui-components-console/popover';
import { KeyValue } from 'src/components/commonComponents/KeyValue';
import { useStore } from 'src/stores/store';
import { Store } from 'src/stores/store.interface';
import { getLocaleDateFromEpoch } from 'src/utils/CommonUtils';
import './updateWFDefinitionReviewStep.scss';
import { TaskDefinition } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import {
	DEFAULT_PREFERENCES as REVIEW_TABLE_DEFAULTS,
	COLLECTION_PREFERENCES as REVIEW_TABLE_PREFS,
} from 'src/pages/ViewWorkflow/ViewWorkflowConstants';
import { getTaskDependencyNamesList } from '../workflowTaskTable/WorkflowTaskTableConstants';
import WorkflowIntentionDisplay from 'src/components/commonComponents/WorkflowIntentionDisplay/WorkflowIntentionDisplay';
import { formatYesNo } from 'src/utils/StringUtils';
import ViewWorkflowDesignerCanvas from '../ViewWorkflowDesignerCanvas/ViewWorkflowDesignerCanvas';
import ViewWorkflowTasksTable from '../ViewWorkflowTasksTable/ViewWorkflowTasksTable';
import WorkflowAttributesTable from 'src/pages/ViewWorkflow/WorkflowAttributesTable';

export default function UpdateWFDefinitionReviewStep() {
	const { taskDefinitions, dependencies } = useStore(
		(state: Store) => state.WFDefinition
	);

	const workflowCreationMetaData = useStore(
		(state: Store) => state.workflowCreationMetaData
	);
	const {
		workflowDefinitionName,
		workflowId,
		domains,
		description,
		entityType,
		createdAt,
		createdBy,
		lastUpdatedBy,
		workflowIntention,
		isReRunAllowed,
		derivedFromId,
	} = workflowCreationMetaData;

	// Map for the expandable KeyValue components
	const WORKFLOW_DETAILS_METADATA_MAP = [
		{ label: 'Workflow Definition Name', value: workflowDefinitionName },
		{ label: 'Workflow Id', value: workflowId },
		{ label: 'Description', value: description },
		{
			label: 'Domains',
			value: domains,
		},
		{ label: 'Entity Type', value: entityType },
		{ label: 'Workflow Intention', value: <WorkflowIntentionDisplay intention={workflowIntention} />},
		{ label: 'Re-Run Allowed', value: formatYesNo(isReRunAllowed) },
		{ label: 'Created At', value: getLocaleDateFromEpoch(createdAt) },
		{ label: 'Created By', value: createdBy },
		{ label: 'Last Updated By', value: lastUpdatedBy },
		...(derivedFromId ? [{ label: 'Derived From Workflow', value: derivedFromId }] : []),
	];

	const MAX_VISIBLE_DEPS = 3;

	const dependenciesColumn = useMemo(() => [{
		id: 'dependencies',
		header: 'Dependencies',
		cell: (item: TaskDefinition) => {
			const names = getTaskDependencyNamesList(item.taskId, dependencies, taskDefinitions);
			
			if (names.length === 0) return '-';
			if (names.length <= MAX_VISIBLE_DEPS) return names.join(', ');
			const visible = names.slice(0, MAX_VISIBLE_DEPS).join(', ');
			const remaining = names.length - MAX_VISIBLE_DEPS;
			
			return (
				<span>
					{visible},{' '}
					<Popover
						size="medium"
						triggerType="text"
						content={
							<SpaceBetween size="xxs">
								{names.map((name, i) => (
									<div key={i}>{name}</div>
								))}
							</SpaceBetween>
						}
					>
						+{remaining} more
					</Popover>
				</span>
			);
		},
	}], [dependencies, taskDefinitions]);

	const reviewTableDefaults = {
		...REVIEW_TABLE_DEFAULTS,
		pageSize: 10,
		contentDisplay: [
			...(REVIEW_TABLE_DEFAULTS.contentDisplay || []),
			{ id: 'dependencies', visible: true },
		],
	};

	return (
		<Box>
			<Box>
				Review the Workflow attributes and design before publishing the workflow
			</Box>
			<SpaceBetween size="m">
				<Container
					id="workflowDetails"
					header={<Header variant="h2">Workflow Details</Header>}
				>
					<ColumnLayout columns={4} variant="text-grid" borders="vertical">
						{WORKFLOW_DETAILS_METADATA_MAP.map((item, index) => (
							<KeyValue key={index} label={item.label}>
								{item.value}
							</KeyValue>
						))}
					</ColumnLayout>
				</Container>
				<ViewWorkflowDesignerCanvas
					taskDefinitions={taskDefinitions}
					dependencies={dependencies}
					containerId="workflowDesigner"
				/>
				<ViewWorkflowTasksTable
					taskDefinitions={taskDefinitions}
					extraColumns={dependenciesColumn}
					defaultPreferences={reviewTableDefaults}
					collectionPreferences={REVIEW_TABLE_PREFS}
				/>
				<WorkflowAttributesTable
					taskDefinitions={taskDefinitions}
					workflowId={workflowId}
				/>
			</SpaceBetween>
		</Box>
	);
}
